import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { useAuth } from '../contexts/AuthContext';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import { 
  getCashBalance, 
  getCashMovements, 
  createCashAdjustment,
  createCashArqueo,
  getDailyCashReport,
  createDepositoBanco 
} from '../api/client';
import { formatCurrency, formatDate } from '../utils/formatters';
import apiClient from '../api/client';

const CashControl = () => {
  const { user, isAdmin } = useAuth();
  const { exchangeRate } = useExchangeRate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // State for cash balance
  const [cashBalance, setCashBalance] = useState(null);
  
  // State for movements
  const [movements, setMovements] = useState([]);
  const [movementFilter, setMovementFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });
  
  // State for daily report
  const [showDailyReport, setShowDailyReport] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dailyReport, setDailyReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  // State for cash adjustment modal
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentForm, setAdjustmentForm] = useState({
    description: '',
    amount_usd: '',
    amount_ves: '',
    notes: ''
  });

  // Users map para nombres
  const [usersMap, setUsersMap] = useState({});

  useEffect(() => {
    apiClient.get('/users').then(res => {
      const map = {};
      (res.data || []).forEach(u => { map[u.id] = u.full_name || u.username; });
      setUsersMap(map);
    }).catch(() => {});
  }, []);

  // State para depósito al banco
  const [showDepositoModal, setShowDepositoModal] = useState(false);
  const [depositoForm, setDepositoForm] = useState({
    amount_ves: '',
    reference: '',
    notes: 'Depósito de efectivo al banco'
  });

  // State para arqueo de caja
  const [showArqueoModal, setShowArqueoModal] = useState(false);
  const [arqueoForm, setArqueoForm] = useState({
    target_usd: '',
    target_ves: '',
    notes: 'Arqueo de caja'
  });

  useEffect(() => {
    fetchCashData();
  }, []);

  useEffect(() => {
    fetchMovements();
  }, [dateRange, movementFilter]);

  const fetchCashData = async () => {
    try {
      setLoading(true);
      const balance = await getCashBalance();
      setCashBalance(balance);
    } catch (err) {
      console.error('Error fetching cash data:', err);
      setError('Error al cargar datos de efectivo');
    } finally {
      setLoading(false);
    }
  };

  const fetchMovements = async () => {
    try {
      const startDate = dateRange.startDate.toISOString().split('T')[0];
      const endDate = dateRange.endDate.toISOString().split('T')[0];
      const movementType = movementFilter === 'all' ? null : movementFilter;
      
      const movs = await getCashMovements(startDate, endDate, movementType);
      setMovements(movs);
    } catch (err) {
      console.error('Error fetching movements:', err);
      setError('Error al cargar movimientos');
    }
  };

  const handleDailyReport = async () => {
    try {
      setLoadingReport(true);
      const date = selectedDate.toISOString().split('T')[0];
      const report = await getDailyCashReport(date);
      setDailyReport(report);
      setShowDailyReport(true);
    } catch (err) {
      console.error('Error fetching daily report:', err);
      alert('Error al generar reporte diario');
    } finally {
      setLoadingReport(false);
    }
  };

  const handleAdjustmentSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAdmin) {
      alert('Solo los administradores pueden hacer ajustes de efectivo');
      return;
    }

    try {
      const adjustmentData = {
        movement_type: "adjustment",
        description: adjustmentForm.description,
        amount_usd: parseFloat(adjustmentForm.amount_usd) || 0,
        amount_ves: parseFloat(adjustmentForm.amount_ves) || 0,
        exchange_rate: exchangeRate,
        movement_date: new Date().toISOString().split('T')[0],
        notes: adjustmentForm.notes
      };

      await createCashAdjustment(adjustmentData);
      
      // Reset form and close modal
      setAdjustmentForm({
        description: '',
        amount_usd: '',
        amount_ves: '',
        notes: ''
      });
      setShowAdjustmentModal(false);
      
      // Refresh data
      await fetchCashData();
      await fetchMovements();
      
      alert('Ajuste de efectivo registrado exitosamente');
      
    } catch (err) {
      console.error('Error creating adjustment:', err);
      
      // Improved error message handling
      let errorMessage = 'Error desconocido';
      
      if (err.response) {
        // Server responded with error status
        if (err.response.data) {
          if (typeof err.response.data === 'string') {
            errorMessage = err.response.data;
          } else if (err.response.data.detail) {
            errorMessage = err.response.data.detail;
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          } else {
            errorMessage = `Error del servidor (${err.response.status})`;
          }
        } else {
          errorMessage = `Error HTTP ${err.response.status}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      alert('Error al registrar ajuste: ' + errorMessage);
    }
  };

  const handleDepositoSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        amount_ves: parseFloat(depositoForm.amount_ves),
        reference: depositoForm.reference,
        notes: depositoForm.notes,
        exchange_rate: exchangeRate,
        movement_date: new Date().toISOString().split('T')[0]
      };
      const result = await createDepositoBanco(data);
      setDepositoForm({ amount_ves: '', reference: '', notes: 'Depósito de efectivo al banco' });
      setShowDepositoModal(false);
      await fetchCashData();
      await fetchMovements();
      alert(`✅ Depósito registrado: Bs. ${result.amount_ves.toLocaleString('es-VE', {minimumFractionDigits:2})}`);
    } catch (err) {
      alert('Error al registrar depósito: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleArqueoSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      alert('Solo los administradores pueden hacer arqueos de caja');
      return;
    }
    try {
      const arqueoData = {
        target_usd: parseFloat(arqueoForm.target_usd) || 0,
        target_ves: parseFloat(arqueoForm.target_ves) || 0,
        notes: arqueoForm.notes,
        exchange_rate: exchangeRate,
        movement_date: new Date().toISOString().split('T')[0]
      };
      const result = await createCashArqueo(arqueoData);
      setArqueoForm({ target_usd: '', target_ves: '', notes: 'Arqueo de caja' });
      setShowArqueoModal(false);
      await fetchCashData();
      await fetchMovements();
      alert(`Arqueo realizado. Ajuste: USD ${result.diff_usd} | VES ${result.diff_ves}`);
    } catch (err) {
      let errorMessage = err.response?.data?.detail || err.message || 'Error desconocido';
      alert('Error al realizar arqueo: ' + errorMessage);
    }
  };

  const getMovementTypeIcon = (type) => {
    switch (type) {
      case 'sale': return '💰';
      case 'expense': return '💸';
      case 'adjustment': return '⚖️';
      case 'deposito_banco': return '🏛️';
      default: return '📋';
    }
  };

  const getMovementTypeLabel = (type) => {
    switch (type) {
      case 'sale': return 'Venta';
      case 'expense': return 'Gasto';
      case 'adjustment': return 'Ajuste';
      case 'deposito_banco': return 'Depósito Banco';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="loading-spinner"></div>
            <span className="ml-2 text-gray-600">Cargando control de efectivo...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="text-5xl">💰</div>
            <div>
              <h1 className="text-3xl font-bold text-blue-900">Control de Efectivo</h1>
              <p className="text-gray-600 mt-1">
                Gestión global del efectivo en caja - USD y VES
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleDailyReport}
              className="btn-outline"
              disabled={loadingReport}
            >
              📊 Reporte Diario
            </button>
            {isAdmin && (
              <>
                <button
                  onClick={() => setShowAdjustmentModal(true)}
                  className="btn-primary"
                >
                  ⚖️ Ajuste Manual
                </button>
                <button
                  onClick={() => {
                    setArqueoForm({
                      target_usd: '',
                      target_ves: '',
                      notes: 'Arqueo de caja'
                    });
                    setShowArqueoModal(true);
                  }}
                  className="btn-primary"
                  style={{backgroundColor: '#6f42c1'}}
                >
                  🏦 Arqueo de Caja
                </button>
                <button
                  onClick={() => setShowDepositoModal(true)}
                  className="btn-primary"
                  style={{backgroundColor: '#0d6efd'}}
                >
                  🏛️ Depositar al Banco
                </button>
              </>
            )}
            <button
              onClick={fetchCashData}
              className="btn-outline"
            >
              🔄 Actualizar
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="text-red-600 text-xl mr-3">⚠️</div>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Cash Balance Cards */}
        {cashBalance && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* USD Balance */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl">💵</span>
                    <h3 className="text-lg font-semibold text-gray-900">Efectivo USD</h3>
                  </div>
                  <p className="text-3xl font-bold text-green-600">
                    ${cashBalance.total_usd.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Última actualización: {formatDate(cashBalance.last_updated)}
                  </p>
                </div>
                <div className="text-4xl text-green-500">🏦</div>
              </div>
            </div>

            {/* VES Balance */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl">💸</span>
                    <h3 className="text-lg font-semibold text-gray-900">Efectivo VES</h3>
                  </div>
                  <p className="text-3xl font-bold text-blue-600">
                    Bs. {(cashBalance.total_ves || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Tasa actual: {exchangeRate} VES/USD
                  </p>
                </div>
                <div className="text-4xl text-blue-500">🏦</div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtros de Movimientos</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Date Range */}
            <div>
              <label className="form-label">Fecha de inicio</label>
              <DatePicker
                selected={dateRange.startDate}
                onChange={(date) => setDateRange(prev => ({ ...prev, startDate: date }))}
                className="form-input w-full"
                dateFormat="dd/MM/yyyy"
              />
            </div>
            
            <div>
              <label className="form-label">Fecha de fin</label>
              <DatePicker
                selected={dateRange.endDate}
                onChange={(date) => setDateRange(prev => ({ ...prev, endDate: date }))}
                className="form-input w-full"
                dateFormat="dd/MM/yyyy"
              />
            </div>

            {/* Movement Type Filter */}
            <div>
              <label className="form-label">Tipo de movimiento</label>
              <select
                value={movementFilter}
                onChange={(e) => setMovementFilter(e.target.value)}
                className="form-input w-full"
              >
                <option value="all">Todos los movimientos</option>
                <option value="sale">Solo ventas</option>
                <option value="expense">Solo gastos</option>
                <option value="adjustment">Solo ajustes</option>
              </select>
            </div>
          </div>
        </div>

        {/* Cash Movements Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Movimientos de Efectivo ({movements.length})
            </h3>
          </div>
          
          {movements.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No hay movimientos
              </h3>
              <p className="text-gray-600">
                No se encontraron movimientos para el período seleccionado.
              </p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Descripción</th>
                    <th>USD</th>
                    <th>VES</th>
                    <th>Tienda</th>
                    <th>Usuario</th>
                    <th>Referencia</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((movement) => (
                    <tr key={movement.id}>
                      <td>
                        <span className="text-sm">
                          {formatDate(movement.movement_date)}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{getMovementTypeIcon(movement.movement_type)}</span>
                          <span className="text-sm font-medium">
                            {getMovementTypeLabel(movement.movement_type)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="text-sm">{movement.description}</span>
                      </td>
                      <td>
                        <span className={`font-semibold ${
                          movement.movement_type === 'expense' ? 'text-red-600' :
                          movement.movement_type === 'adjustment' && movement.amount_usd < 0 ? 'text-red-600' :
                          'text-green-600'
                        }`}>
                          {movement.movement_type === 'expense' ? '-' :
                           movement.movement_type === 'adjustment' && movement.amount_usd < 0 ? '-' : '+'}
                          ${Math.abs(movement.amount_usd).toFixed(2)}
                        </span>
                      </td>
                      <td>
                        <span className={`font-semibold ${
                          movement.movement_type === 'expense' ? 'text-red-600' :
                          movement.movement_type === 'adjustment' && movement.amount_ves < 0 ? 'text-red-600' :
                          'text-green-600'
                        }`}>
                          {movement.movement_type === 'expense' ? '-' :
                           movement.movement_type === 'adjustment' && movement.amount_ves < 0 ? '-' : '+'}
                          Bs. {Math.abs(movement.amount_ves).toFixed(2)}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm">{movement.store_name || 'N/A'}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-600">
                          {movement.user_name || usersMap[movement.user_id] || (movement.user_id ? movement.user_id.substring(0, 8) : 'Sistema')}
                        </span>
                      </td>
                      <td>
                        <span className="text-xs font-mono text-gray-500">
                          {movement.reference_id ? movement.reference_id.substring(0, 8) : 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Depósito al Banco Modal */}
        {showDepositoModal && (
          <div className="modal-overlay" onClick={() => setShowDepositoModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">🏛️ Depositar Efectivo al Banco</h2>
                <button onClick={() => setShowDepositoModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>
              <form onSubmit={handleDepositoSubmit}>
                <div className="modal-body space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    <strong>Efectivo VES disponible:</strong> Bs. {cashBalance ? (cashBalance.total_ves || 0).toLocaleString('es-VE', {minimumFractionDigits:2}) : '0.00'}
                  </div>
                  <p className="text-sm text-gray-600">
                    Este monto saldrá del efectivo disponible y quedará registrado para conciliarlo con el depósito bancario.
                  </p>
                  <div className="form-group">
                    <label className="form-label">Monto a depositar (Bs.) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={depositoForm.amount_ves}
                      onChange={(e) => setDepositoForm(prev => ({ ...prev, amount_ves: e.target.value }))}
                      className="form-input w-full"
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Número de referencia del depósito (opcional)</label>
                    <input
                      type="text"
                      value={depositoForm.reference}
                      onChange={(e) => setDepositoForm(prev => ({ ...prev, reference: e.target.value }))}
                      className="form-input w-full"
                      placeholder="Ej: 0000123456"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notas</label>
                    <input
                      type="text"
                      value={depositoForm.notes}
                      onChange={(e) => setDepositoForm(prev => ({ ...prev, notes: e.target.value }))}
                      className="form-input w-full"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" onClick={() => setShowDepositoModal(false)} className="btn-outline">Cancelar</button>
                  <button type="submit" className="btn-primary" style={{backgroundColor:'#0d6efd'}}>🏛️ Registrar Depósito</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Arqueo de Caja Modal */}
        {showArqueoModal && (
          <div className="modal-overlay" onClick={() => setShowArqueoModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">🏦 Arqueo de Caja</h2>
                <button onClick={() => setShowArqueoModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>
              <form onSubmit={handleArqueoSubmit}>
                <div className="modal-body space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    <strong>Saldo actual:</strong><br/>
                    USD: <strong>${cashBalance ? cashBalance.total_usd.toFixed(2) : '0.00'}</strong> &nbsp;|&nbsp;
                    VES: <strong>Bs. {cashBalance ? (cashBalance.total_ves || 0).toLocaleString('es-VE', {minimumFractionDigits:2}) : '0.00'}</strong>
                  </div>
                  <p className="text-sm text-gray-600">
                    Ingresa el saldo real contado. El sistema calculará el ajuste necesario automáticamente.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Saldo real USD</label>
                      <input
                        type="number"
                        step="0.01"
                        value={arqueoForm.target_usd}
                        onChange={(e) => setArqueoForm(prev => ({ ...prev, target_usd: e.target.value }))}
                        className="form-input w-full"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Saldo real VES (Bs.)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={arqueoForm.target_ves}
                        onChange={(e) => setArqueoForm(prev => ({ ...prev, target_ves: e.target.value }))}
                        className="form-input w-full"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notas</label>
                    <input
                      type="text"
                      value={arqueoForm.notes}
                      onChange={(e) => setArqueoForm(prev => ({ ...prev, notes: e.target.value }))}
                      className="form-input w-full"
                      placeholder="Ej: Arqueo fin de semana"
                    />
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                    ⚠️ El sistema creará un ajuste automático para igualar el saldo al valor ingresado. Si ingresas 0, el saldo quedará en cero.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" onClick={() => setShowArqueoModal(false)} className="btn-outline">Cancelar</button>
                  <button type="submit" className="btn-primary" style={{backgroundColor:'#6f42c1'}}>🏦 Realizar Arqueo</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Cash Adjustment Modal */}
        {showAdjustmentModal && (
          <div className="modal-overlay" onClick={() => setShowAdjustmentModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">⚖️ Ajuste Manual de Efectivo</h2>
                <button
                  onClick={() => setShowAdjustmentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleAdjustmentSubmit}>
                <div className="modal-body space-y-4">
                  <div className="form-group">
                    <label className="form-label">Descripción del ajuste *</label>
                    <input
                      type="text"
                      value={adjustmentForm.description}
                      onChange={(e) => setAdjustmentForm(prev => ({ ...prev, description: e.target.value }))}
                      className="form-input w-full"
                      placeholder="Ej: Corrección por arqueo de caja"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Monto USD</label>
                      <input
                        type="number"
                        step="0.01"
                        value={adjustmentForm.amount_usd}
                        onChange={(e) => setAdjustmentForm(prev => ({ ...prev, amount_usd: e.target.value }))}
                        className="form-input w-full"
                        placeholder="0.00"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Positivo para añadir, negativo para restar
                      </p>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Monto VES</label>
                      <input
                        type="number"
                        step="0.01"
                        value={adjustmentForm.amount_ves}
                        onChange={(e) => setAdjustmentForm(prev => ({ ...prev, amount_ves: e.target.value }))}
                        className="form-input w-full"
                        placeholder="0.00"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Positivo para añadir, negativo para restar
                      </p>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Notas adicionales</label>
                    <textarea
                      value={adjustmentForm.notes}
                      onChange={(e) => setAdjustmentForm(prev => ({ ...prev, notes: e.target.value }))}
                      className="form-input w-full"
                      rows="3"
                      placeholder="Información adicional sobre el ajuste..."
                    />
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <div className="text-yellow-600">⚠️</div>
                      <div className="text-sm text-yellow-800">
                        <strong>Nota importante:</strong> Los ajustes manuales deben usarse solo para correcciones 
                        contables. Se enviará una notificación de Telegram del ajuste realizado.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={() => setShowAdjustmentModal(false)}
                    className="btn-outline"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    ⚖️ Registrar Ajuste
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Daily Report Modal */}
        {showDailyReport && dailyReport && (
          <div className="modal-overlay" onClick={() => setShowDailyReport(false)}>
            <div className="modal-content max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">📊 Reporte Diario de Efectivo</h2>
                <button
                  onClick={() => setShowDailyReport(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="modal-body">
                {/* Report Header */}
                <div className="bg-blue-50 p-4 rounded-lg mb-6">
                  <h4 className="font-medium text-blue-900 mb-2">
                    Fecha: {formatDate(dailyReport.date)}
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-blue-700">Balance actual USD:</span>
                      <p className="text-blue-900 font-semibold">
                        ${dailyReport.current_balance.total_usd.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-blue-700">Balance actual VES:</span>
                      <p className="text-blue-900 font-semibold">
                        Bs. {dailyReport.current_balance.total_ves.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h5 className="font-medium text-green-800 mb-2">💰 Ventas</h5>
                    <p className="text-sm text-green-700">Cantidad: {dailyReport.summary.sales.count}</p>
                    <p className="text-sm text-green-700">USD: ${dailyReport.summary.sales.total_usd.toFixed(2)}</p>
                    <p className="text-sm text-green-700">VES: Bs. {dailyReport.summary.sales.total_ves.toFixed(2)}</p>
                  </div>

                  <div className="bg-red-50 p-4 rounded-lg">
                    <h5 className="font-medium text-red-800 mb-2">💸 Gastos</h5>
                    <p className="text-sm text-red-700">Cantidad: {dailyReport.summary.expenses.count}</p>
                    <p className="text-sm text-red-700">USD: ${dailyReport.summary.expenses.total_usd.toFixed(2)}</p>
                    <p className="text-sm text-red-700">VES: Bs. {dailyReport.summary.expenses.total_ves.toFixed(2)}</p>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h5 className="font-medium text-yellow-800 mb-2">⚖️ Ajustes</h5>
                    <p className="text-sm text-yellow-700">Cantidad: {dailyReport.summary.adjustments.count}</p>
                    <p className="text-sm text-yellow-700">USD: ${dailyReport.summary.adjustments.total_usd.toFixed(2)}</p>
                    <p className="text-sm text-yellow-700">VES: Bs. {dailyReport.summary.adjustments.total_ves.toFixed(2)}</p>
                  </div>
                </div>

                {/* Net Change */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h5 className="font-medium text-gray-800 mb-2">📈 Cambio Neto del Día</h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">USD:</span>
                      <span className={`ml-2 font-semibold ${
                        dailyReport.summary.net_change.usd >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        ${dailyReport.summary.net_change.usd.toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">VES:</span>
                      <span className={`ml-2 font-semibold ${
                        dailyReport.summary.net_change.ves >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        Bs. {dailyReport.summary.net_change.ves.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Movements List */}
                {dailyReport.movements.length > 0 && (
                  <div>
                    <h5 className="font-medium text-gray-800 mb-3">Detalle de Movimientos</h5>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Tipo</th>
                            <th>Descripción</th>
                            <th>USD</th>
                            <th>VES</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dailyReport.movements.map((movement) => (
                            <tr key={movement.id}>
                              <td>
                                <div className="flex items-center space-x-2">
                                  <span>{getMovementTypeIcon(movement.movement_type)}</span>
                                  <span className="text-sm">{getMovementTypeLabel(movement.movement_type)}</span>
                                </div>
                              </td>
                              <td className="text-sm">{movement.description}</td>
                              <td className={`font-semibold ${
                                movement.movement_type === 'expense' ? 'text-red-600' : 'text-green-600'
                              }`}>
                                {movement.movement_type === 'expense' ? '-' : '+'}
                                ${movement.amount_usd.toFixed(2)}
                              </td>
                              <td className={`font-semibold ${
                                movement.movement_type === 'expense' ? 'text-red-600' : 'text-green-600'
                              }`}>
                                {movement.movement_type === 'expense' ? '-' : '+'}
                                Bs. {movement.amount_ves.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  onClick={() => setShowDailyReport(false)}
                  className="btn-outline"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CashControl;